import { AlertTriangle, ArrowRight, CalendarDays, CheckCircle2, Clock, Gauge, ListTodo, Lightbulb, MessageSquare, Plus, RefreshCw, Rocket, Sparkles, Target, Wand2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";
import { PROFILE_TYPES } from "@shared/constants";
import { BarChart } from "@/components/charts/BarChart";
import { Button } from "@/components/ui/button";
import { Badge, Card } from "@/components/ui/misc";
import { Spinner } from "@/components/ui/spinner";
import { TaskSheet } from "@/components/work/TaskSheet";
import { TaskRow } from "@/components/work/TaskRow";
import { useBriefing, useBriefingNarrative } from "@/hooks/useBriefing";
import { useConversations } from "@/hooks/useCoach";
import { useMe } from "@/hooks/useMe";
import { DailyTipCard, GrowthIdeasCard } from "@/components/growth/GrowthCards";
import { PLAN_NAMES } from "@/lib/types";
import { useTasks, useTaskStats } from "@/hooks/useWork";
import { addDays, dayKey, fmtTime, minutesLabel } from "@/lib/dates";
import type { BriefingItem } from "@/lib/types";
import { cn, formatRelative } from "@/lib/utils";
import { LOCALE, t } from "@shared/i18n";

function greeting(tz: string) {
  const part = new Intl.DateTimeFormat(LOCALE, { hour: "numeric", hourCycle: "h23", timeZone: tz }).formatToParts(new Date()).find((p) => p.type === "hour");
  const h = Number(part?.value ?? 12);
  return h < 5 ? t("Bonne nuit") : h < 12 ? t("Bonjour") : h < 18 ? t("Bon après-midi") : t("Bonsoir");
}

function Stat({ label, value, hint, tone, icon: Icon, slate }: { label: string; value: string | number; hint?: string; tone?: "accent" | "success"; icon: typeof Plus; slate?: boolean }) {
  return (
    <div className="glass-card press p-4">
      <div className="flex items-center justify-between">
        <span className={cn("app-icon size-9 [&_svg]:size-[18px]", slate && "app-icon-slate")}>
          <Icon />
        </span>
        <p className={cn("text-[28px] font-bold leading-none tabular-nums", tone === "accent" && "text-accent", tone === "success" && "text-success")}>{value}</p>
      </div>
      <p className="mt-3 text-[13px] font-semibold">{label}</p>
      {hint && <p className="mt-0.5 text-[11px] text-subtle">{hint}</p>}
    </div>
  );
}

function ItemRow({ item }: { item: BriefingItem }) {
  const Icon = item.kind === "risk" ? AlertTriangle : Lightbulb;
  const body = (
    <span className="flex items-start gap-3 rounded-lg p-2.5 hover:bg-surface">
      <Icon className={cn("mt-0.5 size-4 shrink-0", item.kind === "risk" ? (item.severity === "high" ? "text-accent" : "text-warning") : "text-primary")} />
      <span className="min-w-0">
        <span className="block text-sm font-medium">{item.title}</span>
        <span className="block text-[13px] text-muted">{item.detail}</span>
        <span className="mt-0.5 block text-[11px] text-subtle">{t("Pourquoi :")} {item.basis}</span>
      </span>
    </span>
  );
  return item.href ? <Link to={item.href}>{body}</Link> : body;
}

function BriefingCard() {
  const briefing = useBriefing();
  const narrative = useBriefingNarrative();
  const f = briefing.data?.facts;
  const n = briefing.data?.narrative;
  return (
    <Card className="overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/70 bg-gradient-to-r from-[rgb(255_179_71/0.16)] to-transparent px-5 py-4 sm:px-6">
        <div>
          <h2 className="text-[17px] font-bold">{t("Daily Business Briefing")}</h2>
          <p className="text-[12px] text-muted">{t("Calculé à partir de vos tâches, de votre agenda et de vos horaires — aucune donnée inventée.")}</p>
        </div>
        <Button size="sm" variant={n ? "ghost" : "primary"} onClick={() => narrative.mutate(Boolean(n))} loading={narrative.isPending}>
          {n ? <RefreshCw className="size-3.5" /> : <Sparkles className="size-3.5" />} {n ? t("Régénérer la synthèse") : t("Synthèse du coach")}
        </Button>
      </div>
      {briefing.isLoading && (
        <div className="flex justify-center p-10 text-muted">
          <Spinner />
        </div>
      )}
      {f && (
        <div className="grid gap-0 lg:grid-cols-[1.2fr_1fr]">
          <div className="space-y-6 p-6">
            {n && (
              <div className="rounded-2xl border border-brand/40 bg-primary-soft p-4">
                <p className="text-lg font-bold leading-snug">{n.headline}</p>
                <p className="mt-2 text-sm text-muted">{n.advice}</p>
                {n.suggestion && (
                  <p className="mt-3 flex items-start gap-2 text-sm">
                    <ArrowRight className="mt-0.5 size-4 shrink-0 text-primary" /> <span><strong>{t("Maintenant :")}</strong> {n.suggestion}</span>
                  </p>
                )}
                {briefing.data?.narrativeModel && <p className="mt-2 text-[11px] text-subtle">{t("Synthèse générée par")} {briefing.data.narrativeModel} {t("à partir des faits ci-dessous.")}</p>}
              </div>
            )}
            <div>
              <h3 className="text-[13px] font-semibold uppercase tracking-wider text-subtle">{t("Les 3 priorités recommandées")}</h3>
              {f.priorities.length ? (
                <ol className="mt-2 space-y-2">
                  {f.priorities.map((p, i) => (
                    <li key={p.taskId} className="flex gap-3">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-brand text-[12px] font-semibold text-brand-fg">{i + 1}</span>
                      <Link to={`/app/tasks?task=${p.taskId}`} className="min-w-0">
                        <span className="block text-sm font-medium hover:underline">{p.title}</span>
                        <span className="block text-[12px] text-muted">{p.why}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              ) : (
                <p className="mt-2 text-sm text-muted">{t("Aucune tâche ouverte. Ajoutez vos tâches ou lancez une mission.")}</p>
              )}
            </div>
            {(f.risks.length > 0 || f.opportunities.length > 0) && (
              <div>
                <h3 className="text-[13px] font-semibold uppercase tracking-wider text-subtle">{t("Radar : risques et opportunités")}</h3>
                <div className="mt-1">
                  {[...f.risks, ...f.opportunities].slice(0, 5).map((it, i) => (
                    <ItemRow key={i} item={it} />
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="space-y-6 border-t border-border p-6 lg:border-l lg:border-t-0">
            <div>
              <h3 className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wider text-subtle">
                <CalendarDays className="size-3.5" /> {t("Agenda du jour")}
              </h3>
              {f.events.length ? (
                <ul className="mt-2 space-y-1.5">
                  {f.events.map((e) => (
                    <li key={e.id + e.start} className="flex gap-3 text-sm">
                      <span className="w-12 shrink-0 tabular-nums text-muted">{e.allDay ? t("Jour") : fmtTime(e.start)}</span>
                      <span className="truncate">{e.title}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-muted">{t("Aucun rendez-vous aujourd'hui.")}</p>
              )}
            </div>
            <div>
              <h3 className="flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wider text-subtle">
                <Clock className="size-3.5" /> {t("Créneaux libres")}
              </h3>
              {!f.workday ? (
                <p className="mt-2 text-sm text-muted">{t("Jour non travaillé selon vos préférences.")}</p>
              ) : f.freeSlots.length ? (
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {f.freeSlots.map((s) => (
                    <li key={s.start}>
                      <Badge>
                        {fmtTime(s.start)}–{fmtTime(s.end)} · {minutesLabel(s.minutes)}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-sm text-muted">{t("Plus de créneau libre aujourd'hui.")}</p>
              )}
              <Link to="/app/planning" className="mt-3 inline-flex items-center gap-1.5 text-[13px] text-primary hover:underline">
                <Wand2 className="size-3.5" /> {t("Planifier ma journée en un clic")}
              </Link>
            </div>
            <div className="flex gap-6 text-sm">
              <span>
                <span className="font-semibold tabular-nums">{f.dueToday.length}</span> <span className="text-muted">{t("échéance(s) aujourd'hui")}</span>
              </span>
              <span>
                <span className={cn("font-semibold tabular-nums", f.overdue.length && "text-accent")}>{f.overdue.length}</span> <span className="text-muted">{t("en retard")}</span>
              </span>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export function Overview() {
  const { data: me } = useMe();
  const stats = useTaskStats();
  const today = useTasks({ view: "today" });
  const convs = useConversations({ q: "", archived: false });
  const [openTask, setOpenTask] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  if (!me) return null;
  const tz = me.profile.timezone;
  const dateLabel = new Intl.DateTimeFormat(LOCALE, { weekday: "long", day: "numeric", month: "long", timeZone: tz }).format(new Date());
  const s = stats.data;
  const todayKey = dayKey(new Date());
  const chart = Array.from({ length: 14 }, (_, i) => {
    const k = addDays(todayKey, i - 13);
    const n = s?.daily.find((d) => d.day === k)?.n ?? 0;
    return { label: String(Number(k.slice(8))), sublabel: new Intl.DateTimeFormat(LOCALE, { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${k}T12:00:00Z`)), values: { done: n } };
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="animate-fade-up flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wide text-subtle first-letter:uppercase">{dateLabel}</p>
          <h1 className="mt-1.5 text-[34px] font-bold leading-[1.05] tracking-tight sm:text-[44px]">
            {greeting(tz)}, {me.user.name.split(" ")[0]}.
          </h1>
          <p className="mt-3 text-[15px] text-muted">
            {PROFILE_TYPES.find((p) => p.id === me.profile.profileType)?.label ?? t("Entrepreneur")}
            {me.profile.sector ? ` · ${me.profile.sector}` : ""}
            {me.profile.companyName ? ` · ${me.profile.companyName}` : ""}
          </p>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:flex sm:flex-wrap">
          <Button variant="secondary" onClick={() => setCreating(true)}>
            <Plus className="size-4" /> {t("Tâche")}
          </Button>
          <Link to="/app/missions">
            <Button variant="secondary" className="w-full">
              <Rocket className="size-4" /> {t("Mission")}
            </Button>
          </Link>
          <Link to="/app/coach?new=1">
            <Button className="w-full">
              <Sparkles className="size-4" /> {t("Coach")}
            </Button>
          </Link>
        </div>
      </header>

      <div className="stagger mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat icon={ListTodo} label={t("Tâches ouvertes")} value={s?.open ?? "–"} hint={s ? t("{0} estimées", minutesLabel(s.plannedMinutesOpen) || "0 min") : undefined} />
        <Stat icon={AlertTriangle} slate label={t("En retard")} value={s?.overdue ?? "–"} tone={s?.overdue ? "accent" : undefined} />
        <Stat icon={CheckCircle2} label={t("Terminées (7 j)")} value={s?.doneWeek ?? "–"} tone="success" />
        <Stat icon={Gauge} slate label={t("Échéances tenues")} value={s?.doneWeek ? `${Math.round((s.doneOnTimeWeek / s.doneWeek) * 100)} %` : "–"} hint={t("7 derniers jours")} />
      </div>

      <div className="mt-6">
        <BriefingCard />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.4fr]">
        <DailyTipCard />
        <GrowthIdeasCard />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <div className="flex items-center justify-between border-b border-border px-6 py-4">
            <h2 className="flex items-center gap-2 text-[15px] font-semibold">
              <CheckCircle2 className="size-4 text-primary" /> {t("À faire aujourd'hui")}
            </h2>
            <Link to="/app/tasks" className="text-[13px] text-primary hover:underline">
              {t("Toutes les tâches")}
            </Link>
          </div>
          <div className="p-2">
            {today.data?.length ? today.data.slice(0, 8).map((t) => <TaskRow key={t.id} task={t} onOpen={setOpenTask} />) : <p className="px-4 py-8 text-center text-sm text-muted">{today.isLoading ? t("Chargement…") : t("Rien d'urgent aujourd'hui.")}</p>}
          </div>
        </Card>
        <div className="space-y-6">
          <Card className="p-6">
            <h2 className="text-[15px] font-semibold">{t("Tâches terminées — 14 derniers jours")}</h2>
            <div className="mt-3">
              <BarChart title={t("Nombre de tâches terminées par jour sur les 14 derniers jours")} data={chart} series={[{ key: "done", label: t("Terminées"), color: "var(--chart-1)" }]} format={(n) => String(Math.round(n))} />
            </div>
          </Card>
          <Card>
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <h2 className="text-[15px] font-semibold">{t("Conversations récentes")}</h2>
              <Link to="/app/coach" className="text-[13px] text-primary hover:underline">
                {t("Tout voir")}
              </Link>
            </div>
            {convs.data?.length ? (
              <ul className="divide-y divide-border">
                {convs.data.slice(0, 4).map((c) => (
                  <li key={c.id}>
                    <Link to={`/app/coach/${c.id}`} className="flex items-center gap-3 px-6 py-3 hover:bg-surface">
                      <MessageSquare className="size-4 shrink-0 text-subtle" />
                      <span className="flex-1 truncate text-sm">{c.title}</span>
                      <span className="shrink-0 text-[12px] text-subtle">{formatRelative(c.lastMessageAt)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-6 py-6 text-center text-sm text-muted">{t("Aucune conversation.")}</p>
            )}
          </Card>
          <Card className="p-6">
            <div className="flex items-center gap-2">
              <Target className="size-4 text-primary" />
              <h2 className="text-[15px] font-semibold">{t("Objectifs déclarés")}</h2>
            </div>
            {me.profile.goals.length ? (
              <ul className="mt-3 space-y-1.5 text-sm">
                {me.profile.goals.map((g) => (
                  <li key={g} className="flex gap-2">
                    <span className="mt-2 size-1.5 shrink-0 rounded-full bg-brand" />
                    {g}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-muted">{t("Aucun objectif renseigné.")}</p>
            )}
            <p className="mt-4 text-[12px] text-muted">
              {t("IA :")} {me.usage.used}/{me.usage.limit} {t("messages aujourd'hui · formule")} {PLAN_NAMES[me.workspace.plan]}
            </p>
          </Card>
        </div>
      </div>
      <TaskSheet open={creating || Boolean(openTask)} onOpenChange={(o) => { if (!o) { setCreating(false); setOpenTask(null); } }} taskId={openTask} />
    </div>
  );
}
